import React from 'react';
import './PrivacyPolicy.css';

export function PrivacyPolicy() {
    return (
        <div className="privacy-policy-container">
            <header className="privacy-header">
                <h1>WashX Laundry</h1>
                <p className="subtitle">Privacy Policy</p>
            </header>

            <div className="effective-date">
                Effective Date: February 10, 2026
            </div>

            <div className="privacy-content">
                <section>
                    <h2>1. Introduction</h2>
                    <p>
                        Welcome to WashX Laundry ("we," "us," or "our"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our laundry and dry-cleaning services via WhatsApp and our web platform.
                    </p>
                    <p>
                        By using our services, you agree to the collection and use of information in accordance with this policy.
                    </p>
                </section>

                <section>
                    <h2>2. Information We Collect</h2>
                    <p>We collect information that you provide directly to us when using our services:</p>
                    <ul>
                        <li><strong>Personal Information:</strong> Name, phone number, delivery address, and WhatsApp profile information</li>
                        <li><strong>Order Information:</strong> Service type (wash & fold, dry clean, etc.), pickup and delivery preferences, special instructions</li>
                        <li><strong>Communication Data:</strong> Messages sent through WhatsApp, including text, images, and order-related communications</li>
                        <li><strong>Transaction Information:</strong> Order history, payment details (processed securely), and billing information</li>
                        <li><strong>Location Data:</strong> Delivery address and service area information</li>
                    </ul>
                </section>

                <section>
                    <h2>3. How We Use Your Information</h2>
                    <p>We use the collected information for the following purposes:</p>
                    <ul>
                        <li>Process and fulfill your laundry service orders</li>
                        <li>Communicate with you about your orders via WhatsApp</li>
                        <li>Send order confirmations, pickup reminders, and delivery notifications</li>
                        <li>Provide customer support and respond to your inquiries</li>
                        <li>Improve our services and customer experience</li>
                        <li>Send promotional offers and service updates (you can opt out at any time)</li>
                        <li>Prevent fraud and ensure service security</li>
                        <li>Comply with legal obligations and resolve disputes</li>
                    </ul>
                </section>

                <section>
                    <h2>4. WhatsApp Integration</h2>
                    <p>
                        Our services use WhatsApp Business API to communicate with customers. When you interact with us via WhatsApp:
                    </p>
                    <ul>
                        <li>Your phone number and WhatsApp profile information (name, profile picture) are collected</li>
                        <li>Message content is processed to understand your service requests</li>
                        <li>We use WhatsApp's end-to-end encryption for secure communications</li>
                        <li>We do not share your WhatsApp data with third parties for marketing purposes</li>
                        <li>You can stop receiving messages by blocking our WhatsApp number or sending "STOP"</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Data Sharing and Disclosure</h2>
                    <p>We may share your information in the following circumstances:</p>
                    <ul>
                        <li><strong>Service Providers:</strong> Third-party companies that help us operate our business (payment processors, delivery partners)</li>
                        <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or government request</li>
                        <li><strong>Business Transfers:</strong> In connection with any merger, sale of assets, or acquisition of our business</li>
                        <li><strong>With Your Consent:</strong> When you explicitly agree to share information with specific third parties</li>
                    </ul>
                    <p>We do NOT sell your personal information to third parties.</p>
                </section>

                <section>
                    <h2>6. Data Security</h2>
                    <p>
                        We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                    </p>
                    <ul>
                        <li>Encrypted data transmission (HTTPS/SSL)</li>
                        <li>Secure database storage with access controls</li>
                        <li>Regular security audits and updates</li>
                        <li>Staff training on data protection practices</li>
                    </ul>
                    <p>
                        However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee its absolute security.
                    </p>
                </section>

                <section>
                    <h2>7. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy. Specifically:
                    </p>
                    <ul>
                        <li><strong>Order History:</strong> Retained for 2 years for customer service and warranty purposes</li>
                        <li><strong>Account Information:</strong> Retained while your account is active and for 1 year after closure</li>
                        <li><strong>Communication Records:</strong> Retained for 6 months for customer service purposes</li>
                        <li><strong>Transaction Records:</strong> Retained as required by law (typically 7 years for tax purposes)</li>
                    </ul>
                    <p>You can request deletion of your data at any time by contacting us.</p>
                </section>

                <section>
                    <h2>8. Your Rights and Choices</h2>
                    <p>You have the following rights regarding your personal information:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                        <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                        <li><strong>Opt-Out:</strong> Unsubscribe from promotional messages by sending "STOP" via WhatsApp</li>
                        <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format</li>
                        <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where consent was the basis</li>
                    </ul>
                    <p>To exercise these rights, please contact us using the information below.</p>
                </section>

                <section>
                    <h2>9. Children's Privacy</h2>
                    <p>
                        Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately, and we will delete such information.
                    </p>
                </section>

                <section>
                    <h2>10. Third-Party Links</h2>
                    <p>
                        Our communications may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any information.
                    </p>
                </section>

                <section>
                    <h2>11. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by:
                    </p>
                    <ul>
                        <li>Posting the updated policy on our website with a new effective date</li>
                        <li>Sending a notification via WhatsApp to active customers</li>
                        <li>Displaying a notice in our service interface</li>
                    </ul>
                    <p>Your continued use of our services after such changes constitutes acceptance of the updated policy.</p>
                </section>

                <section>
                    <h2>12. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
                    </p>
                </section>

                <section>
                    <h2>13. Contact Us</h2>
                    <div className="contact-info">
                        <p><strong>WashX Laundry (Freshbasket Supermarts)</strong></p>
                        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
                        <ul>
                            <li><strong>WhatsApp:</strong> <a href="https://wa.me/918977768206" target="_blank" rel="noopener noreferrer">+91 89777 68206</a></li>
                            <li><strong>Email:</strong> <a href="mailto:privacy@washx.in">privacy@washx.in</a></li>
                            <li><strong>Website:</strong> <a href="https://communex.duckdns.org">https://communex.duckdns.org</a></li>
                        </ul>
                        <p style={{ marginTop: '15px' }}>
                            <strong>Response Time:</strong> We will respond to your privacy inquiries within 30 days.
                        </p>
                    </div>
                </section>

                <section>
                    <h2>14. Consent</h2>
                    <p>
                        By using our services and providing your information via WhatsApp or our web platform, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy.
                    </p>
                </section>
            </div>

            <footer className="privacy-footer">
                <p>&copy; 2026 WashX Laundry (Freshbasket Supermarts). All rights reserved.</p>
                <p style={{ marginTop: '10px' }}>Last Updated: February 10, 2026</p>
            </footer>
        </div>
    );
}
